import { FC, PropsWithChildren, useEffect, useCallback } from "react";
import { useSetLayout } from "capitalrx-shell";
import { useSelector, useDispatch } from "react-redux";
import { General } from "capitalrx-components-2.0";
import { Breadcrumb, PageAlert } from "capitalrx-components";
import { useLocation } from "react-router";

import { appRoutes } from "../../routes/routes";
import { RootState } from "../../reducers";
import { PageAlertState } from "../../../types/appTypes/general";
import { logEvent, ANALYTIC_EVENTS } from "@/App/utils/analytics";

/**
 * The legacy content layout component properties.
 */
export interface LegacyContentLayoutProps {
  /**
   * Indicates whether the content should be displayed fullscreen.
   */
  readonly fullscreen?: boolean;
}

/**
 * The legacy breadcrumb component.
 */
const LegacyBreadcrumb: FC = (): JSX.Element => {
  const breadcrumbClickHandler = useCallback((breadcrumb: string): void => {
    logEvent(ANALYTIC_EVENTS.MAIN_MENU.breadcrumb, { breadcrumb: breadcrumb });
  }, []);

  return <Breadcrumb extraClasses={"breadcrumb-bar"} routes={appRoutes} onClickFunction={breadcrumbClickHandler} />;
};

/**
 * The legacy content layout.
 * @deprecated To be removed when legacy routes are removed.
 */
export const LegacyContentLayout: FC<PropsWithChildren<LegacyContentLayoutProps>> = ({
  fullscreen = false,
  children,
}: PropsWithChildren<LegacyContentLayoutProps>): JSX.Element => {
  const alertData = useSelector<RootState, PageAlertState>((state) => state.pageAlert);
  const showAlert = General.useAlert();
  const { hidePageAlert } = PageAlert;
  const dispatch = useDispatch();
  const location = useLocation();
  const { PageAlertContainer } = PageAlert;

  // Marshal the legacy alerts to new alerts.
  useEffect(() => {
    if (!alertData) {
      return;
    }

    const { message, type: legacyType, timeout = 5000, show } = alertData;

    if (!show) {
      return;
    }

    let type: General.AlertType = "error";
    switch (legacyType) {
      case "success":
        type = "success";
        break;
      case "warning":
        type = "warning";
        break;
      case "error":
        type = "error";
        break;
      default:
        type = "informational";
        break;
    }

    showAlert({ message, type, style: "banner", timeout });
  }, [alertData]);

  const setLayout = useSetLayout();

  useEffect(() => {
    setLayout(fullscreen ? "fullscreen" : "auto");
  }, [fullscreen]);

  useEffect(() => {
    setTimeout(() => {
      dispatch(hidePageAlert());
    }, 1500);
  }, [location]);

  return (
    <>
      <PageAlertContainer
        iconClass={alertData.iconClass}
        message={alertData.message}
        show={alertData.show}
        timeout={alertData.timeout ?? 5000}
        type={alertData.type}
      />
      {(!fullscreen) && <LegacyBreadcrumb />}
      {children}
    </>
  );
};
